import { Modal, Progress } from 'antd'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Link } from 'react-router-dom'

import { Button, useBulkInProgressStatus } from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'

import styles from './_.module.css'

interface BulkInProgressModalProps {
  transactionId: string
  list?: Assignment[]
  isBulkMarkEdited?: boolean
  isBulkMarkRework?: boolean
  isBulkWorkflow?: boolean
}

const percentConstant = 100

export const BulkInProgressModal = NiceModal.create<BulkInProgressModalProps>(
  ({
    transactionId,
    list,
    isBulkMarkEdited,
    isBulkMarkRework,
    isBulkWorkflow
  }) => {
    const modal = useModal()

    const { data } = useBulkInProgressStatus(transactionId, isBulkWorkflow)
    if (typeof data === 'undefined') {
      return null
    }

    const { transaction_data = [] } = data || {}
    const success =
      data.transaction_data.filter(progress => progress.status === 'success') ||
      []
    const failed =
      transaction_data.filter(progress => progress.status === 'failure') || []
    const inProgress =
      transaction_data.filter(progress => progress.status === 'in-progress') ||
      []
    const total = transaction_data.length > 0 ? transaction_data.length : 1

    const successPercent = Math.floor(
      (success?.length / total) * percentConstant
    )
    const failedPercent = Math.floor((failed?.length / total) * percentConstant)
    const completed = success.length + failed.length === total
    if (completed) {
      modal.resolve({ success, failed })
    }

    let title = `Updating status for the ${pluralize(total, 'assignment')}`

    if (isBulkMarkEdited)
      title = `Marking Reviewed for the ${pluralize(total, 'assignment')}`
    else if (isBulkMarkRework)
      title = `Marking Rework for the ${pluralize(total, 'assignment')}`

    return (
      <Modal
        title={title}
        visible={modal.visible}
        onCancel={modal.remove}
        destroyOnClose={true}
        closable={completed ? true : false}
        maskClosable={false}
        footer={[
          completed ? <Button onClick={modal.remove}>Close</Button> : null
        ]}
        afterClose={modal.remove}
      >
        {!completed ? (
          <div>
            <Progress
              percent={successPercent}
              success={{ percent: failedPercent }}
            />
            <div className={styles.progress_label_container}>
              <span className={styles.progress_label}>
                In Progress: {inProgress.length}
              </span>
              <span className={styles.progress_label}>
                Success: {success.length}
              </span>
              <span className={styles.progress_label}>
                Failed: {failed.length}
              </span>
              <span>Total: {total}</span>
            </div>
          </div>
        ) : (
          <div>
            <p>Your assignments have been successfully updated.</p>
            {failed.length > 0 && (
              <div>
                <p>The following assignments failed:</p>
                <ul className={styles.itemLinkDisplay}>
                  {failed.map(item => {
                    const obj = list?.find(
                      assignment => assignment.id === item.assignment_id
                    )
                    if (!list) {
                      return (
                        <div
                          key={item.assignment_id}
                          className={styles.itemDetails}
                        >
                          <Link
                            to={`/allotment/assignment/${item.assignment_id}/activity`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.assignment_id}
                          </Link>
                          <div className={styles.errorMsg}>
                            {item?.description}
                          </div>
                        </div>
                      )
                    }

                    return (
                      <Link
                        to={`/assignment/${item.assignment_id}/brief`}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={item.assignment_id}
                      >
                        {obj?.assignment_title}
                      </Link>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    )
  }
)
